// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { AuditEvent, auditEventSchema } from '@/audit/api/types';
import { z } from 'zod';

const shipmentAuditEventsResponseSchema = z
  .object({
    // eslint-disable-next-line no-restricted-syntax -- Backend context is json.RawMessage (polymorphic). Parse each tolerantly and filter invalid.
    data: z.array(z.unknown()).transform((events) => {
      return events.reduce<AuditEvent[]>((validEvents, event) => {
        const result = auditEventSchema.safeParse(event);

        if (result.success) {
          return [...validEvents, result.data];
        }

        const error = new Error('Invalid audit event', { cause: event });
        if (import.meta.env.MODE === 'development') {
          console.error('Invalid audit event:', event, result.error);
        }
        Sentry.captureException(error);

        return validEvents;
      }, []);
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, events: data }));

export async function getShipmentAuditEvents(shipmentKey: string) {
  const url = `/v1/shipment/${shipmentKey}/audit`;
  const response = await api.get(url);

  return parseResponse(shipmentAuditEventsResponseSchema, response.data, {
    schemaName: 'ShipmentAuditEvents',
    endpoint: url,
  });
}
